import React from 'react'
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Slider,
  Button,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Clear as ClearIcon } from '@mui/icons-material'
import { tr } from 'date-fns/locale'

const UserFilters = ({ filters, onChange, onReset }) => {
  const handleFilterChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
    })
  }

  const handleCreditRangeChange = (event, newValue) => {
    handleFilterChange('creditRange', newValue)
  }

  const handleDateRangeChange = (field, value) => {
    handleFilterChange('dateRange', {
      ...filters.dateRange,
      [field]: value,
    })
  }

  const creditMarks = [
    { value: 0, label: '0' },
    { value: 100, label: '100' },
    { value: 500, label: '500' },
    { value: 1000, label: '1000' },
    { value: 5000, label: '5000+' },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filtreler
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={onReset}
          >
            Filtreleri Temizle
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Status Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Kullanıcı Durumu</InputLabel>
              <Select
                value={filters.status || 'all'}
                label="Kullanıcı Durumu"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="suspended">Askıda</MenuItem>
                <MenuItem value="banned">Yasaklı</MenuItem>
                <MenuItem value="pending">Beklemede</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Admin Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Kullanıcı Tipi</InputLabel>
              <Select
                value={filters.userType || 'all'}
                label="Kullanıcı Tipi"
                onChange={(e) => handleFilterChange('userType', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">Normal Kullanıcı</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Registration Date Range */}
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Kayıt Başlangıç Tarihi"
              value={filters.dateRange?.startDate || null}
              onChange={(value) => handleDateRangeChange('startDate', value)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={new Date()}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <DatePicker
              label="Kayıt Bitiş Tarihi"
              value={filters.dateRange?.endDate || null}
              onChange={(value) => handleDateRangeChange('endDate', value)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              maxDate={new Date()}
              minDate={filters.dateRange?.startDate || undefined}
            />
          </Grid>

          {/* Credit Range Filter */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Kredi Bakiyesi Aralığı
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={filters.creditRange || [0, 1000]}
                onChange={handleCreditRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value} kredi`}
                min={0}
                max={5000}
                step={50}
                marks={creditMarks}
                sx={{ mt: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {filters.creditRange?.[0] || 0} kredi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filters.creditRange?.[1] || 1000} kredi
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Transaction Count Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>İşlem Sayısı</InputLabel>
              <Select
                value={filters.transactionCount || 'all'}
                label="İşlem Sayısı"
                onChange={(e) => handleFilterChange('transactionCount', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="none">Hiç işlem yapmamış</MenuItem>
                <MenuItem value="low">1-5 işlem</MenuItem>
                <MenuItem value="medium">6-20 işlem</MenuItem>
                <MenuItem value="high">20+ işlem</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Last Login Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Son Giriş</InputLabel>
              <Select
                value={filters.lastLogin || 'all'}
                label="Son Giriş"
                onChange={(e) => handleFilterChange('lastLogin', e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="today">Bugün</MenuItem>
                <MenuItem value="week">Son 7 gün</MenuItem>
                <MenuItem value="month">Son 30 gün</MenuItem>
                <MenuItem value="never">Hiçbir zaman</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Applied Filters Summary */}
        {(filters.status !== 'all' || 
          filters.userType !== 'all' || 
          filters.creditRange?.[0] !== 0 || 
          filters.creditRange?.[1] !== 1000 ||
          filters.dateRange?.startDate ||
          filters.dateRange?.endDate ||
          filters.transactionCount !== 'all' ||
          filters.lastLogin !== 'all') && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Aktif Filtreler:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {filters.status !== 'all' && (
                <Typography variant="body2" sx={{ 
                  px: 1, py: 0.5, 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}>
                  Durum: {filters.status}
                </Typography>
              )}
              {filters.userType !== 'all' && (
                <Typography variant="body2" sx={{ 
                  px: 1, py: 0.5, 
                  bgcolor: 'secondary.main', 
                  color: 'white', 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}>
                  Tip: {filters.userType}
                </Typography>
              )}
              {(filters.creditRange?.[0] !== 0 || filters.creditRange?.[1] !== 1000) && (
                <Typography variant="body2" sx={{ 
                  px: 1, py: 0.5, 
                  bgcolor: 'success.main', 
                  color: 'white', 
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}>
                  Kredi: {filters.creditRange?.[0]}-{filters.creditRange?.[1]}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default UserFilters